import React, { useCallback, useEffect, useRef, useState } from "react";
import { fetchAppConfig } from "./appConfigApi";
import { postPortfolioAiChat } from "./portfolioAiChatApi";
import "./portfolioAiChat.css";

const STARTER_PROMPTS = [
  "What is the total MRC for projects in Fiber?",
  "How many networking jobs need to be scheduled?",
  "Status of work order 14001",
];

function stripMarkdownAsterisks(text) {
  let s = String(text || "");
  s = s.replace(/\*\*([^*]+)\*\*/g, "$1");
  s = s.replace(/(?<!\*)\*([^*\n]+)\*(?!\*)/g, "$1");
  return s;
}

function apiErrorMessage(err) {
  const data = err?.response?.data;
  if (data?.error) return String(data.error);
  if (err?.message) return String(err.message);
  return "Request failed.";
}

export default function PortfolioAiChatPanel() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([]);
  const [lastContext, setLastContext] = useState(null);
  const [usage, setUsage] = useState(null);
  const [configured, setConfigured] = useState(true);
  const listRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    fetchAppConfig()
      .then((cfg) => {
        if (cancelled) return;
        const ac = cfg?.aiChat;
        if (ac) {
          setUsage({
            today: ac.usageToday ?? 0,
            limit: ac.dailyLimit ?? 25,
            remaining: ac.remaining ?? 0,
          });
          setConfigured(ac.configured !== false);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const scrollToEnd = useCallback(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  const sendMessage = useCallback(
    async (text) => {
      const trimmed = String(text || "").trim();
      if (!trimmed || busy) return;

      const userEntry = { role: "user", content: trimmed };
      const history = messages.map((m) => ({ role: m.role, content: m.content }));
      setMessages((prev) => [...prev, userEntry]);
      setInput("");
      setBusy(true);
      setTimeout(scrollToEnd, 0);

      try {
        const data = await postPortfolioAiChat({
          message: trimmed,
          history,
        });
        setLastContext(data.context || null);
        if (data.usage) setUsage(data.usage);
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: stripMarkdownAsterisks(data.reply || "(No response)") },
        ]);
      } catch (err) {
        setMessages((prev) => [
          ...prev,
          { role: "error", content: apiErrorMessage(err) },
        ]);
      } finally {
        setBusy(false);
        setTimeout(scrollToEnd, 0);
      }
    },
    [busy, messages, scrollToEnd]
  );

  const onSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  if (!open) {
    return (
      <div className="pb-portfolio-ai" aria-label="Portfolio assistant">
        <button
          type="button"
          className="pb-portfolio-ai-fab"
          onClick={() => setOpen(true)}
          title="Portfolio assistant"
        >
          AI
        </button>
      </div>
    );
  }

  return (
    <div className="pb-portfolio-ai" aria-label="Portfolio assistant">
      <div className="pb-portfolio-ai-panel" role="dialog" aria-labelledby="pb-portfolio-ai-title">
        <header className="pb-portfolio-ai-header">
          <h2 id="pb-portfolio-ai-title" className="pb-portfolio-ai-title">
            Portfolio assistant
          </h2>
          <button
            type="button"
            className="pb-portfolio-ai-close"
            onClick={() => setOpen(false)}
            aria-label="Close"
          >
            ×
          </button>
        </header>
        <div className="pb-portfolio-ai-messages" ref={listRef}>
          {messages.length === 0 ? (
            <div className="pb-portfolio-ai-empty">
              <p style={{ margin: 0 }}>
                Ask about commercial projects, phases, fiber work, work orders, or customers.
                Answers use live data from the board (not the web).
              </p>
              {STARTER_PROMPTS.map((p) => (
                <button
                  key={p}
                  type="button"
                  style={{
                    display: "block",
                    marginTop: 8,
                    textAlign: "left",
                    background: "none",
                    border: 0,
                    padding: 0,
                    color: "#0f766e",
                    cursor: "pointer",
                    fontSize: 13,
                    textDecoration: "underline",
                  }}
                  onClick={() => sendMessage(p)}
                  disabled={busy}
                >
                  {p}
                </button>
              ))}
            </div>
          ) : null}
          {messages.map((m, i) => (
            <div
              key={`${m.role}-${i}`}
              className={`pb-portfolio-ai-msg pb-portfolio-ai-msg--${m.role}`}
            >
              {m.content}
            </div>
          ))}
          {busy ? (
            <div className="pb-portfolio-ai-msg pb-portfolio-ai-msg--assistant">Thinking…</div>
          ) : null}
        </div>
        {lastContext ? (
          <p className="pb-portfolio-ai-meta">
            Context: {lastContext.projects_in_context ?? "?"} projects
            {lastContext.commercial_total != null
              ? ` · ${lastContext.commercial_total} commercial total`
              : ""}
          </p>
        ) : null}
        {usage ? (
          <p className="pb-portfolio-ai-meta">
            Today (UTC): {usage.today ?? 0} / {usage.limit ?? "?"} questions
            {usage.remaining != null ? ` · ${usage.remaining} left` : ""}
          </p>
        ) : null}
        {!configured ? (
          <p className="pb-portfolio-ai-meta" style={{ color: "#b45309" }}>
            AI is not configured. Ask an administrator to add an API key under Settings → AI.
          </p>
        ) : null}
        <form className="pb-portfolio-ai-form" onSubmit={onSubmit}>
          <textarea
            className="pb-portfolio-ai-input"
            rows={2}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about the commercial portfolio…"
            disabled={busy}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                onSubmit(e);
              }
            }}
          />
          <button
            type="submit"
            className="pb-portfolio-ai-send"
            disabled={
              busy ||
              !input.trim() ||
              !configured ||
              (usage && usage.remaining != null && usage.remaining <= 0)
            }
          >
            Send
          </button>
        </form>
      </div>
    </div>
  );
}
